import DownloadIcon from "@mui/icons-material/Download";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { Box, Button, Link, Typography } from "@mui/material";
import QRCodeStyling from "qr-code-styling";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminDialog } from "@/components/common";
import { getCurrentRegion, Region } from "@/utils/environment";

interface MobileLinkAuthTokenProps {
  authToken: string;
  linkId: number;
  storeId: number;
  linkName: string;
  selectedCustomerId?: number | null;
}

interface Destination {
  id: "dev" | "ua";
  baseUrl: string;
}

const DEV_DESTINATION: Destination = {
  id: "dev",
  baseUrl: "https://mobile-craveretail-dev.web.app",
};
const UA_DESTINATION: Destination = {
  id: "ua",
  baseUrl: "https://ua.mobile.crave-cloud.com",
};
const QR_LOGO_URL = "/images/UA_Logo_black.png";
const QR_SIZE = 3000;
const buildQrUrl = (baseUrl: string, authToken: string, storeId: number) =>
  `${baseUrl}/?auth=web-link:%20${encodeURIComponent(authToken)}&storeId=${storeId}`;

const sanitizeFileNameSegment = (segment: string | number | null | undefined, fallback: string) => {
  if (segment === null || segment === undefined) {
    return fallback;
  }

  const rawSegment = String(segment).trim();
  if (!rawSegment) {
    return fallback;
  }

  const normalized = rawSegment
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  const sanitized = normalized.replace(/\s+/g, "-").replace(/[^a-z0-9-_]/g, "");
  return sanitized || fallback;
};
const buildDownloadFileBaseName = (name: string, id: number | string) =>
  `qrcode-${sanitizeFileNameSegment(name, "link")}-(${sanitizeFileNameSegment(id, "id")})`;
const buildBlobFromRawData = (rawData: Blob | ArrayBuffer | ArrayBufferView, type?: string) => {
  if (rawData instanceof Blob) {
    return rawData;
  }

  const blobPart =
    rawData instanceof ArrayBuffer
      ? rawData
      : Uint8Array.from(new Uint8Array(rawData.buffer, rawData.byteOffset, rawData.byteLength))
          .buffer;

  return new Blob([blobPart], type ? { type } : undefined);
};

interface DestinationQrCodeProps {
  destination: Destination;
  authToken: string;
  storeId: number;
  linkName: string;
  linkId: number;
}

const DestinationQrCode: React.FC<DestinationQrCodeProps> = ({
  destination,
  authToken,
  storeId,
  linkName,
  linkId,
}) => {
  const { t } = useTranslation();
  const [qrImageSrc, setQrImageSrc] = useState<string | null>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);
  const qrUrl = buildQrUrl(destination.baseUrl, authToken, storeId);
  const downloadFileBaseName = `${buildDownloadFileBaseName(linkName, linkId)}-${destination.id}`;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setQrImageSrc(null);
    const qrCode = new QRCodeStyling({
      width: QR_SIZE,
      height: QR_SIZE,
      type: "svg",
      data: qrUrl,
      ...(destination.id === "ua" ? { image: QR_LOGO_URL } : {}),
      imageOptions: { crossOrigin: "anonymous", margin: 0 },
      dotsOptions: { type: "rounded", color: "#111827" },
      backgroundOptions: { color: "#ffffff" },
    });
    qrCodeRef.current = qrCode;

    const generateImage = async () => {
      try {
        const rawData = await qrCode.getRawData("svg");
        if (cancelled || !rawData) return;
        objectUrl = URL.createObjectURL(buildBlobFromRawData(rawData));
        setQrImageSrc(objectUrl);
      } catch {
        // Keep the destination URL available if QR generation fails.
      }
    };
    void generateImage();
    return () => {
      cancelled = true;
      qrCodeRef.current = null;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [qrUrl, destination.id]);

  const handleDownloadClick = async () => {
    const qrCode = qrCodeRef.current;
    if (!qrCode) return;
    try {
      await qrCode.download({ name: downloadFileBaseName, extension: "png" });
    } catch {
      try {
        const rawData = await qrCode.getRawData("png");
        if (!rawData) return;
        const url = URL.createObjectURL(buildBlobFromRawData(rawData, "image/png"));
        try {
          const link = document.createElement("a");
          link.href = url;
          link.download = `${downloadFileBaseName}.png`;
          link.click();
        } finally {
          URL.revokeObjectURL(url);
        }
      } catch {
        // Leave the URL available when the browser cannot download the image.
      }
    }
  };

  return (
    <Box
      component="section"
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, minWidth: 0 }}
    >
      <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere", textAlign: "center" }}>
        {new URL(destination.baseUrl).hostname}
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: 280,
          aspectRatio: "1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {qrImageSrc ? (
          <Box
            component="img"
            src={qrImageSrc}
            alt={t("features.mobileLink.mobileLinkAuthToken.accessibility.qrCodeWebLink")}
            sx={{ display: "block", width: "100%", height: "100%", backgroundColor: "#ffffff" }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {t("features.mobileLink.mobileLinkAuthToken.text.generatingQrCode")}
          </Typography>
        )}
      </Box>
      <Link
        href={qrUrl}
        target="_blank"
        rel="noopener noreferrer"
        variant="caption"
        sx={{ overflowWrap: "anywhere", textAlign: "center" }}
      >
        {qrUrl}
      </Link>
      <Typography variant="caption" color="text.secondary">
        {t("features.mobileLink.mobileLinkAuthToken.text.scanOpenLink")}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon fontSize="small" />}
        onClick={() => void handleDownloadClick()}
        disabled={!qrImageSrc}
      >
        {t("features.mobileLink.mobileLinkAuthToken.text.downloadQrCode")}
      </Button>
    </Box>
  );
};

const MobileLinkAuthToken: React.FC<MobileLinkAuthTokenProps> = ({
  authToken,
  linkId,
  storeId,
  linkName,
  selectedCustomerId,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const region = useMemo(() => getCurrentRegion(), []);
  const destinations = useMemo(() => {
    if (region === Region.DEV) {
      return selectedCustomerId === 47 ? [DEV_DESTINATION, UA_DESTINATION] : [DEV_DESTINATION];
    }
    return region === Region.NA && selectedCustomerId === 5 ? [UA_DESTINATION] : [];
  }, [region, selectedCustomerId]);
  const enabled = destinations.length > 0;

  useEffect(() => {
    setOpen(false);
  }, [authToken, storeId, selectedCustomerId]);

  return (
    <>
      <Box
        component="span"
        role={enabled ? "button" : undefined}
        tabIndex={enabled ? 0 : undefined}
        onClick={enabled ? () => setOpen(true) : undefined}
        onKeyDown={
          enabled
            ? (event: React.KeyboardEvent<HTMLElement>) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setOpen(true);
                }
              }
            : undefined
        }
        aria-label={
          enabled
            ? t("features.mobileLink.mobileLinkAuthToken.accessibility.showQrCode", { linkName })
            : undefined
        }
        aria-expanded={enabled ? open : undefined}
        aria-haspopup={enabled ? "dialog" : undefined}
        sx={{
          cursor: enabled ? "pointer" : "text",
          color: enabled ? "primary.main" : "text.primary",
          textDecoration: enabled ? "underline" : "none",
          fontWeight: 500,
        }}
      >
        {authToken}
      </Box>
      {enabled && (
        <AdminDialog
          icon={<QrCodeIcon />}
          open={open}
          onClose={() => setOpen(false)}
          title={linkName}
          maxWidth={destinations.length > 1 ? "md" : "sm"}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: destinations.length > 1 ? "1fr 1fr" : "1fr" },
              gap: 3,
            }}
          >
            {destinations.map((destination) => (
              <DestinationQrCode
                key={`${destination.id}-${authToken}-${storeId}`}
                destination={destination}
                authToken={authToken}
                storeId={storeId}
                linkName={linkName}
                linkId={linkId}
              />
            ))}
          </Box>
        </AdminDialog>
      )}
    </>
  );
};

export default MobileLinkAuthToken;
